import { FC } from 'react';
import { Badge } from 'reactstrap';
import { FreshnessPillProps } from './types';

/**
 * Header freshness indicator (README §Global layout / app shell): "Index
 * v{manifestVersion} · ~{age} old", green tint when fresh, warn-gold when
 * `/ingest/status.stale === true`. Hardcoded approximations of the design
 * reference's token values — this codebase has no CSS-variable design-system
 * package to pull from (see SelectionPanel.tsx for the same approach).
 */

// gold-100 / gold-700 (README §Design Tokens "Status pills": pale fill +
// deep text) for the stale tint; green for fresh.
const GOLD_100 = '#FEF0D9';
const GOLD_700 = '#B25E09';

const PILL_BASE_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 28,
  fontWeight: 600,
  fontSize: 12,
  padding: '0 10px',
  borderRadius: 999,
};

const FRESH_STYLE: React.CSSProperties = {
  ...PILL_BASE_STYLE,
  background: '#D1FAE5',
  color: '#066E52',
};

const STALE_STYLE: React.CSSProperties = {
  ...PILL_BASE_STYLE,
  background: GOLD_100,
  color: GOLD_700,
};

const DOT_STYLE: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: 'currentColor',
  flexShrink: 0,
};

/** Humanizes an ingest age in seconds into a short "~Nm/h/d" fragment. */
export const humanizeAge = (ageSeconds: number): string => {
  if (!Number.isFinite(ageSeconds) || ageSeconds < 0) return 'unknown';
  const minutes = Math.floor(ageSeconds / 60);
  if (minutes < 1) return '<1m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
};

const FreshnessPill: FC<FreshnessPillProps> = ({ ingest }) => {
  // No status loaded yet (still fetching, or the ingest/status call failed) —
  // stay silent rather than render a misleading pill.
  if (!ingest) return null;

  return (
    <Badge pill style={ingest.stale ? STALE_STYLE : FRESH_STYLE}>
      <span style={DOT_STYLE} />
      Index v{ingest.manifestVersion} · ~{humanizeAge(ingest.ageSeconds)} old
    </Badge>
  );
};

export default FreshnessPill;
